import React, { useEffect, useMemo, useState } from 'react';
import { Card, Table, Button, Badge, Row, Col, Form } from 'react-bootstrap';
import { useNavigate, useSearchParams } from 'react-router-dom';
import API from '../services/api';
import Pager from './Pager';
import { formatDay, formatDateTime, todayDR } from '../utils/dates';

const PAGE_SIZE = 20;
const money = (n) => new Intl.NumberFormat('es-DO', { style: 'currency', currency: 'DOP' }).format(n || 0);
const norm = (s) => String(s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
const MONTHS = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];
const monthLabel = (m) => `${MONTHS[Number(m.slice(5, 7)) - 1]} ${m.slice(0, 4)}`;
const shiftMonth = (m, by) => { const d = new Date(Date.UTC(Number(m.slice(0, 4)), Number(m.slice(5, 7)) - 1 + by, 1)); return d.toISOString().slice(0, 7); };

// The same kinds and colors as the dashboard's "Despachos por documento" chart (SalesDashboard.js DOC_KINDS).
const KINDS = [
  { key: 'factura', label: 'Con factura', color: '#4ADE80' },
  { key: 'acuerdo', label: 'Acuerdo', color: '#60A5FA' },
  { key: 'conduce', label: 'Solo conduce', color: '#F5B700' },
  { key: 'none', label: 'Sin documento', color: '#F87171' },
];
// A despacho counts under a kind when it has tanks there (a conduce a deal pays in part is in both "Acuerdo" and "Solo conduce").
const tanksIn = (d, kind) => (kind === 'acuerdo' ? d.acuerdo : kind === 'conduce' ? d.conduce : d.kind === kind ? d.units : 0);
const SYNC = { vinculado: 'Vinculado a pedido', sin_pedido: 'Sin pedido en Ventas', pendiente: 'Sincronización pendiente', error: 'Ventas lo rechazó' };

const DocLink = ({ doc }) => (
  <a href={`/${doc.type === 'factura' ? 'invoices' : 'conduces'}/${doc.id}/print`} target="_blank" rel="noreferrer">{doc.number}</a>
);

// Admins: one month's despachos as Manifold Manager recorded them, each with the Ventas document its note names (backend
// GET /dashboard/despachos, services/dispatchedTanks.js). Reached from the dashboard chart; ?tipo= picks the kind, ?mes= the month.
const DespachosPage = () => {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const month = /^\d{4}-\d{2}$/.test(params.get('mes') || '') ? params.get('mes') : todayDR().slice(0, 7);
  const kind = KINDS.some(k => k.key === params.get('tipo')) ? params.get('tipo') : '';
  const [rows, setRows] = useState(null);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);

  const setParam = (key, value) => { const next = new URLSearchParams(params); if (value) next.set(key, value); else next.delete(key); setParams(next, { replace: true }); };

  useEffect(() => {
    setRows(null); setError('');
    API.get('/dashboard/despachos', { params: { month } })
      .then(({ data }) => setRows(data.despachos))
      .catch(err => setError(err.response?.data?.message || 'No se pudieron cargar los despachos.'));
  }, [month]);
  useEffect(() => { setPage(1); }, [month, kind, search]);

  const totals = useMemo(() => Object.fromEntries(KINDS.map(k => {
    const list = (rows || []).filter(d => tanksIn(d, k.key) > 0);
    return [k.key, { despachos: list.length, tanks: list.reduce((s, d) => s + tanksIn(d, k.key), 0) }];
  })), [rows]);
  const allTanks = (rows || []).reduce((s, d) => s + d.units, 0);

  const term = norm(search.trim());
  const filtered = useMemo(() => (rows || []).filter(d => (!kind || tanksIn(d, kind) > 0)
    && (!term || [d.noteNumber, d.dispatchedBy, d.doc?.number, d.doc?.client?.name, ...(d.clients || []).map(c => c.name)].some(v => norm(v).includes(term)))), [rows, kind, term]);
  const pages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const current = Math.min(page, pages);
  const slice = filtered.slice((current - 1) * PAGE_SIZE, current * PAGE_SIZE);

  return (
    <div className="container-fluid p-4">
      <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
        <h2 className="app-title" style={{ textAlign: 'left', margin: 0 }}>Despachos — {monthLabel(month)}</h2>
        <div className="d-flex gap-2">
          <Button variant="outline-secondary" onClick={() => setParam('mes', shiftMonth(month, -1))}>‹ Mes anterior</Button>
          <Button variant="outline-secondary" disabled={month >= todayDR().slice(0, 7)} onClick={() => setParam('mes', shiftMonth(month, 1))}>Mes siguiente ›</Button>
        </div>
      </div>
      <div className="text-muted small mb-3">
        Lo que salió de la planta según Manifold Manager, con el documento de Ventas que nombra la nota de cada despacho. Para
        los que no nombran ninguno se muestran documentos posibles: el mismo número con otra letra, o una factura / conduce del
        mismo cliente hecho tres días antes o después. Son solo sugerencias.
      </div>

      {error && <div className="alert alert-danger">{error}</div>}
      {!error && rows === null && <div className="text-muted py-3">Cargando...</div>}
      {rows && (
        <>
          <Row className="mb-3">
            {KINDS.map(k => (
              <Col key={k.key} xs={6} lg={3} className="mb-3">
                <Card className="shadow-sm p-3 dashboard-card dashboard-card-link h-100" onClick={() => setParam('tipo', kind === k.key ? '' : k.key)}
                  style={{ outline: kind === k.key ? `2px solid ${k.color}` : undefined }}>
                  <div className="text-muted small"><span style={{ color: k.color }}>●</span> {k.label}</div>
                  <h2 className="mt-2 mb-0" style={{ color: k.color }}>{totals[k.key].tanks.toLocaleString('es-DO')}</h2>
                  <div className="text-muted small">
                    tanques · {totals[k.key].despachos} despacho{totals[k.key].despachos === 1 ? '' : 's'}
                    {allTanks ? ` · ${Math.round((totals[k.key].tanks / allTanks) * 100)}%` : ''}
                  </div>
                </Card>
              </Col>
            ))}
          </Row>

          <Card className="shadow-sm p-3">
            <div className="d-flex flex-wrap gap-2 mb-3 align-items-center">
              <Form.Control style={{ maxWidth: 320 }} placeholder="Buscar por nota, cliente, documento o quién despachó..." value={search} onChange={e => setSearch(e.target.value)} />
              {kind && <Badge bg="info">{KINDS.find(k => k.key === kind).label}</Badge>}
              {kind && <Button size="sm" variant="link" className="p-0" onClick={() => setParam('tipo', '')}>Ver todos</Button>}
              {kind === 'factura' && <Button size="sm" variant="link" className="p-0" onClick={() => navigate(`/invoices?despachos=${month}`)}>Ver estas facturas en Facturas</Button>}
              {kind === 'conduce' && <Button size="sm" variant="link" className="p-0" onClick={() => navigate('/conduces?tab=sin_facturar')}>Conduces sin facturar</Button>}
              {kind === 'acuerdo' && <Button size="sm" variant="link" className="p-0" onClick={() => navigate('/conduces?tab=acuerdos')}>Acuerdos</Button>}
            </div>
            <div className="table-responsive">
              <Table hover className="mb-0 align-middle">
                <thead>
                  <tr><th>Fecha</th><th>Nota</th><th className="text-end">Tanques</th><th>Para (Manifold)</th><th>Despacho</th><th>Documento en Ventas</th></tr>
                </thead>
                <tbody>
                  {slice.map(d => {
                    const k = KINDS.find(x => x.key === (d.kind === 'conduce' && d.acuerdo > 0 && !d.conduce ? 'acuerdo' : d.kind));
                    return (
                      <tr key={d.id}>
                        <td className="small" style={{ whiteSpace: 'nowrap' }}>{formatDateTime(d.date)}</td>
                        <td>
                          <strong>{d.noteNumber || <span className="text-muted">(sin nota)</span>}</strong>
                          <div className="small" style={{ color: k.color }}>{k.label}</div>
                        </td>
                        <td className="text-end">
                          {d.units}
                          {d.kind === 'conduce' && d.acuerdo > 0 && d.conduce > 0 && <div className="small text-muted">{d.acuerdo} acuerdo · {d.conduce} sin facturar</div>}
                        </td>
                        <td className="small">
                          {(d.clients || []).length ? d.clients.map(c => <div key={c.name}>{c.name}{d.clients.length > 1 ? ` (${c.units})` : ''}</div>) : <span className="text-muted">—</span>}
                          {d.ventasClients?.length > 0 && <div className="text-muted">En Ventas: {d.ventasClients.map(c => c.name).join(', ')}</div>}
                        </td>
                        <td className="small">
                          {d.method && <div>{d.method === 'retiro' ? 'Retiro en planta' : 'Entrega'}{d.delivered ? ` · entregado${d.deliveredAt ? ` ${formatDay(d.deliveredAt)}` : ''}` : d.method === 'entrega' ? ' · por entregar' : ''}</div>}
                          {d.received != null && d.received !== d.units && <div>Recibió {d.received} de {d.units}</div>}
                          {d.returned > 0 && <div>Devueltos: {d.returned}</div>}
                          {d.dispatchedBy && <div className="text-muted">Por {d.dispatchedBy}</div>}
                          {d.ventas?.pedidoNumber && <div className="text-muted">Pedido {d.ventas.pedidoNumber}</div>}
                          {d.ventas?.status && d.ventas.status !== 'vinculado' && <div className="text-muted" title={d.ventas.reason || undefined}>{SYNC[d.ventas.status] || d.ventas.status}</div>}
                        </td>
                        <td className="small">
                          {d.doc ? (
                            <>
                              <div>{d.doc.type === 'factura' ? 'Factura' : 'Conduce'} <DocLink doc={d.doc} /> · {d.doc.client?.name || '—'}</div>
                              <div className="text-muted">{formatDay(d.doc.date)}{d.doc.total ? ` · ${money(d.doc.total)}` : ''}</div>
                              {d.doc.facturas?.length > 0 && <div>Facturado en {d.doc.facturas.map((f, i) => <span key={f.id}>{i ? ', ' : ''}<DocLink doc={{ ...f, type: 'factura' }} /></span>)}</div>}
                            </>
                          ) : (
                            <>
                              <span className="text-muted">Ningún documento con ese número.</span>
                              {(d.possible || []).map(p => (
                                <div key={p.id}>
                                  <span style={{ color: 'var(--mm-cyan)' }}>Posible: </span>
                                  {p.type === 'factura' ? 'factura' : 'conduce'} <DocLink doc={p} /> · {p.client || '—'} · {formatDay(p.date)}{p.total ? ` · ${money(p.total)}` : ''}
                                  <span className="text-muted"> ({p.reason === 'number' ? 'mismo número' : 'mismo cliente, misma fecha'})</span>
                                </div>
                              ))}
                            </>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                  {slice.length === 0 && (
                    <tr><td colSpan={6} className="text-center text-muted py-4">{rows.length ? 'Ningún despacho coincide.' : 'No hubo despachos ese mes.'}</td></tr>
                  )}
                </tbody>
              </Table>
            </div>
            {pages > 1 && (
              <div className="d-flex justify-content-between align-items-center mt-3">
                <span className="text-muted small">Página {current} de {pages} — {filtered.length} despacho{filtered.length === 1 ? '' : 's'}</span>
                <Pager page={current} pages={pages} onChange={setPage} />
              </div>
            )}
          </Card>
        </>
      )}
    </div>
  );
};

export default DespachosPage;
